import {
  Button,
  Card,
  CardBody,
  DescriptionList,
  EmptyState,
  formatDate,
  formatPercent,
  Grid,
  Inline,
  LinkButton,
  Money,
  Panel,
  SearchInput,
  Skeleton,
  Stack,
  SummaryStat,
  SummaryStats,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableLoading,
  TableRow,
  TableTotalRow,
  Toolbar,
  ToolbarCount,
  ToolbarItem,
  ToolbarSpacer,
  VisuallyHidden,
} from '@hkdevs/design-system';
import type { ReactNode } from 'react';
import type { components } from '../../api/schema.gen';

// Line items as on a check's detail and on pending pay (pc-dbb.4, pc-dbb.5): a table
// whose rows expand to the line's fields in their product's groups, and on pending pay a
// search (check pages lost theirs in A16).

export type LineItem = components['schemas']['LineItem'];
export type LineItemColumn = components['schemas']['LineItemColumn'];
type LineItemRow = components['schemas']['LineItemRow'];
type Format = LineItemColumn['format'];

/** The table every tenant starts with, for while the columns are loading. */
export const DEFAULT_COLUMNS: readonly LineItemColumn[] = [
  { key: 'customer', label: 'Customer', format: 'text', fixed: true },
  { key: 'productType', label: 'Product Type', format: 'text', fixed: true },
  { key: 'INSTALL DATE', label: 'Install Date', format: 'date', fixed: false },
  { key: 'amount', label: 'Amount Earned', format: 'money', fixed: true },
];

const NUMERIC: ReadonlySet<Format> = new Set(['money', 'number', 'decimal', 'percent']);
const isNumeric = (column: LineItemColumn) =>
  column.key === 'amount' || (!column.fixed && NUMERIC.has(column.format));
const labelOf = (columns: readonly LineItemColumn[], key: string, fallback: string) =>
  columns.find((column) => column.key === key)?.label ?? fallback;

/**
 * A value as shown, by its format. The API cleans values (ISO dates, decimal strings,
 * percent numbers); one that didn't parse arrives as uploaded and shows as it is.
 */
export function formatValue(value: string | undefined, format: Format): ReactNode {
  if (value === undefined || value.trim() === '') return '—';
  const number = Number(value);
  const numeric = Number.isFinite(number);
  switch (format) {
    case 'date': {
      const date = formatDate(value);
      return date === '—' ? value : date;
    }
    case 'money':
      return numeric ? <Money value={value} /> : value;
    case 'number':
      return numeric ? number.toLocaleString('en-US', { maximumFractionDigits: 0 }) : value;
    case 'decimal':
      return numeric
        ? number.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : value;
    case 'percent':
      return numeric ? formatPercent(value) : value;
    default:
      return value;
  }
}

/** The line items whose customer or product type contains the query. */
export function filterLineItems(items: readonly LineItem[], query: string) {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  return items.filter(
    (item) =>
      (item.customer ?? '').toLowerCase().includes(q) || item.productType.toLowerCase().includes(q),
  );
}

export const dollars = (cents: number) => (cents / 100).toFixed(2);

/** A field's value as shown: money as Money (negatives in danger), dates and percentages
 * formatted; numbers as the API sends them. */
function fieldValue(row: LineItemRow) {
  if (row.format === 'money' || row.format === 'date' || row.format === 'percent') {
    return formatValue(row.value, row.format);
  }
  return row.value;
}

/** One cell of a line item's row, by its column. The customer is the row header, with a
 * label like every other cell, so a card shows it as a label/value pair. */
function LineItemCell({ item, column }: { item: LineItem; column: LineItemColumn }) {
  switch (column.key) {
    case 'customer':
      return (
        <TableCell rowHeader label={column.label} tone="strong">
          {item.customer ?? item.productType}
        </TableCell>
      );
    case 'productType':
      return (
        <TableCell label={column.label} tone="muted">
          {item.productType}
        </TableCell>
      );
    case 'amount':
      return (
        <TableCell label={column.label} numeric tone="strong">
          <Money value={dollars(item.amountCents)} />
        </TableCell>
      );
    default:
      return (
        <TableCell label={column.label} numeric={isNumeric(column)} tone="muted">
          {formatValue(item.values[column.key], column.format)}
        </TableCell>
      );
  }
}

/** A line item's fields, a Panel per group, in the product's schema order. */
function LineItemFields({ item, onClose }: { item: LineItem; onClose: () => void }) {
  return (
    <Stack gap={4}>
      <Grid minColumnWidth="lg" gap={4} align="start">
        {item.groups.map((group) => (
          <Panel key={group.title} title={group.title} headingLevel={2}>
            <DescriptionList
              items={group.rows.map((row) => {
                const value = fieldValue(row);
                return {
                  key: row.label,
                  term: row.emphasize ? <strong>{row.label}</strong> : row.label,
                  description: row.emphasize ? <strong>{value}</strong> : value,
                };
              })}
            />
          </Panel>
        ))}
      </Grid>
      <Inline justify="end">
        <LinkButton onClick={onClose}>Close</LinkButton>
      </Inline>
    </Stack>
  );
}

export interface LineItemListProps {
  /** The line items, in the API's order. */
  lineItems: readonly LineItem[];
  /** The whole list's total, in cents (the Total row, when not searching). */
  totalCents: number;
  /** The search, by customer or product type (Pending Pay). Leave both out for no search
   * (a check's detail, A16): every line item shows. */
  query?: string | undefined;
  onQueryChange?: ((query: string) => void) | undefined;
  /** The expanded line item's id, or null: one at a time. */
  expandedId: string | null;
  onExpandedChange: (id: string | null) => void;
  /** The total row's label. Default "Total". */
  totalLabel?: string;
  /** The table's columns, in order, with their labels (the response's `columns`). */
  columns: readonly LineItemColumn[];
}

/** The line-item table, with its Total row, and a search with "No matching line items"
 * when `onQueryChange` is given. */
export function LineItemList({
  lineItems,
  totalCents,
  query = '',
  onQueryChange,
  expandedId,
  onExpandedChange,
  totalLabel = 'Total',
  columns,
}: LineItemListProps) {
  const searchable = onQueryChange !== undefined;
  const filtering = searchable && query.trim() !== '';
  const shown = filtering ? filterLineItems(lineItems, query) : lineItems;
  const customerLabel = labelOf(columns, 'customer', 'Customer').toLowerCase();
  const productTypeLabel = labelOf(columns, 'productType', 'Product Type').toLowerCase();

  return (
    <Stack gap={3}>
      <Toolbar aria-label={searchable ? 'Search line items' : 'Line item count'}>
        {searchable && (
          <ToolbarItem size="search">
            <SearchInput
              label={`Search line items by ${customerLabel} or ${productTypeLabel}`}
              placeholder={`Search by ${customerLabel} or ${productTypeLabel}`}
              value={query}
              onValueChange={onQueryChange}
            />
          </ToolbarItem>
        )}
        <ToolbarSpacer />
        <ToolbarCount>
          {shown.length} {shown.length === 1 ? 'line item' : 'line items'}
        </ToolbarCount>
      </Toolbar>
      <Card flush>
        {shown.length > 0 || !filtering ? (
          <Table aria-label="Line items" responsive="cards">
            <TableHead>
              {columns.map((column) => (
                <TableCell key={column.key} numeric={isNumeric(column)}>
                  {column.label}
                </TableCell>
              ))}
              <TableCell width="44px">
                <VisuallyHidden>Details</VisuallyHidden>
              </TableCell>
            </TableHead>
            <TableBody>
              {shown.map((item) => {
                const expanded = expandedId === item.id;
                const customer = item.customer ?? item.productType;
                return (
                  <TableRow
                    key={item.id}
                    label={`Details for ${customer}`}
                    expanded={expanded}
                    onToggle={() => onExpandedChange(expanded ? null : item.id)}
                    detail={<LineItemFields item={item} onClose={() => onExpandedChange(null)} />}
                  >
                    {columns.map((column) => (
                      <LineItemCell key={column.key} item={item} column={column} />
                    ))}
                  </TableRow>
                );
              })}
              {!filtering && (
                <TableTotalRow>
                  <TableCell rowHeader colSpan={columns.length - 1}>
                    {totalLabel}
                  </TableCell>
                  <TableCell numeric>
                    <Money value={dollars(totalCents)} />
                  </TableCell>
                  <TableCell />
                </TableTotalRow>
              )}
            </TableBody>
          </Table>
        ) : (
          <CardBody>
            <EmptyState
              title="No matching line items"
              description="No line items match your search. Try a different term."
              icon="search"
              headingLevel={2}
              actions={
                <Button size="sm" variant="secondary" onClick={() => onQueryChange?.('')}>
                  Clear search
                </Button>
              }
            />
          </CardBody>
        )}
      </Card>
    </Stack>
  );
}

/** Placeholders in a line-item page's shape: the summary's two figures and the table. */
export function LineItemsLoading({
  dateLabel,
  totalLabel,
}: {
  dateLabel: string;
  totalLabel: string;
}) {
  return (
    <>
      <SummaryStats aria-hidden="true">
        <SummaryStat label={dateLabel} value={<Skeleton width="8rem" />} size="lg" />
        <SummaryStat label={totalLabel} value={<Skeleton width="8rem" />} size="lg" />
      </SummaryStats>
      <Card flush>
        <Table aria-label="Line items" busy>
          <TableHead>
            {DEFAULT_COLUMNS.map((column) => (
              <TableCell key={column.key} numeric={isNumeric(column)}>
                {column.label}
              </TableCell>
            ))}
          </TableHead>
          <TableBody>
            <TableLoading colSpan={DEFAULT_COLUMNS.length} rows={5} label="Loading line items" />
          </TableBody>
        </Table>
      </Card>
    </>
  );
}
